import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Sidebar } from 'primereact/sidebar';
import { Dropdown } from 'primereact/dropdown';
import { Menu } from 'primereact/menu';
import ExportSettings from '../ExportSettings';
import DefaultPrioritiesSettings from '../DefaultPrioritiesSettings';
import configSettings from '../../configSettings.json';

export const EnrichmentResultTab = ({
    handleDownload,
    handleDownloadAbu,
    enrichedRows,
    grouping,
    resultColumns,
    customCell,
    onApplyDefaultPriorities // <-- added
}) => {
    const LAST_PRESET_KEY = 'enrichment_presets_last';
    const PRESETS_KEY = 'enrichment_presets';

    // Helper function to get stored presets
    const getStoredPresets = () => {
        try {
            const raw = localStorage.getItem(PRESETS_KEY);
            const obj = raw ? JSON.parse(raw) : null;
            return obj && typeof obj === 'object' ? obj : {};
        } catch (err) {
            console.warn('Error reading presets from localStorage:', err);
            return {};
        }
    };

    // Helper function to get last selected preset
    const getLastSelectedPreset = () => {
        try {
            return localStorage.getItem(LAST_PRESET_KEY) || 'Default';
        } catch (err) {
            console.warn('Error reading last preset from localStorage:', err);
            return 'Default';
        }
    };

    // Ensure default presets from config are present in localStorage
    const ensureDefaultPresets = () => {
        const storedPresets = getStoredPresets();
        const defaultPresets = configSettings?.DefaultPresets;
        if (defaultPresets && typeof defaultPresets === 'object') {
            let updated = false;
            for (const key of Object.keys(defaultPresets)) {
                if (!(key in storedPresets)) {
                    storedPresets[key] = defaultPresets[key];
                    updated = true;
                }
            }
            if (updated) {
                try {
                    localStorage.setItem(PRESETS_KEY, JSON.stringify(storedPresets));
                    // Select the first missing default preset
                    const firstDefault = Object.keys(defaultPresets)[0];
                    if (firstDefault) {
                        localStorage.setItem(LAST_PRESET_KEY, firstDefault);
                    }
                } catch (err) {
                    console.warn('Error saving default presets to localStorage:', err);
                }
            }
        }
    };

    // Run once on component mount
    ensureDefaultPresets();

    // Initialize state with proper values from localStorage
    const initializeState = () => {
        const storedPresets = getStoredPresets();
        const storedPresetNames = Object.keys(storedPresets);
        const lastPreset = getLastSelectedPreset();
        const validPresets = ['Default', ...storedPresetNames];
        
        const options = [
            { label: 'Default', value: 'Default' },
            ...storedPresetNames.map(name => ({ label: name, value: name }))
        ];

        const selectedValue = validPresets.includes(lastPreset) ? lastPreset : 
                             storedPresetNames.length > 0 ? storedPresetNames[0] : 'Default';

        return { options, selectedValue };
    };

    const { options: initialOptions, selectedValue: initialSelectedValue } = initializeState();

    const [settingsVisible, setSettingsVisible] = useState(false);
    const [presetOptions, setPresetOptions] = useState(initialOptions);
    const [selectedPreset, setSelectedPreset] = useState(initialSelectedValue);

    // Helper function to save last selected preset
    const saveLastSelectedPreset = (preset) => {
        try {
            localStorage.setItem(LAST_PRESET_KEY, preset);
        } catch (err) {
            console.warn('Error saving last preset to localStorage:', err);
        }
    };

    // Function to load presets and set the selected one
    const loadPresets = () => {
        const storedPresets = getStoredPresets();
        const storedPresetNames = Object.keys(storedPresets);
        
        // Build preset options
        const options = [
            { label: 'Default', value: 'Default' },
            ...storedPresetNames.map(name => ({ label: name, value: name }))
        ];
        
        setPresetOptions(options);

        // Determine which preset to select
        const lastPreset = getLastSelectedPreset();
        const validPresets = ['Default', ...storedPresetNames];
        
        if (validPresets.includes(lastPreset)) {
            setSelectedPreset(lastPreset);
        } else if (storedPresetNames.length > 0) {
            // If last preset is invalid, select the first stored preset
            setSelectedPreset(storedPresetNames[0]);
        } else {
            setSelectedPreset('Default');
        }
    };

    // Reload presets when settings sidebar is opened
    useEffect(() => {
        if (settingsVisible) {
            loadPresets();
        }
        // eslint-disable-next-line
    }, [settingsVisible]);

    // Save last selected preset when it changes
    useEffect(() => {
        if (selectedPreset) {
            saveLastSelectedPreset(selectedPreset);
        }
    }, [selectedPreset]);

    // Build merged column list in the same order as rendered table (grouping first)
    const getMergedColumns = () => {
        const allCols = Array.isArray(configSettings?.AllColumns) ? [...configSettings.AllColumns] : [];

        // Start with grouping then resultColumns (preserve rendered order)
        let columns = [];
        if (Array.isArray(grouping) && grouping.length > 0) {
            columns.push(...grouping);
        }
        if (Array.isArray(resultColumns) && resultColumns.length > 0) {
            const filtered = resultColumns.filter(col => !(grouping && grouping.includes && grouping.includes(col)));
            columns.push(...filtered);
        }

        // If nothing from grouping/resultColumns, start from default AllColumns
        if (columns.length === 0 && allCols.length > 0) {
            columns = [...allCols];
        } else if (allCols.length > 0) {
            // Append any missing columns from AllColumns
            allCols.forEach(col => {
                if (!columns.includes(col)) columns.push(col);
            });
        }

        // Ensure "Units" is present at position 0
        if (!columns.includes('Units')) {
            columns = ['Units', ...columns];
        } else {
            const idx = columns.indexOf('Units');
            if (idx > 0) {
                columns.splice(idx, 1);
                columns.unshift('Units');
            }
        }

        return columns;
    };

    // Get columns for a specific preset — remove any columns not present in enrichedRows
    const getColumnsForPreset = (preset) => {
        const merged = getMergedColumns();

        // Build set of available columns present in enrichedRows
        const available = new Set();
        if (Array.isArray(enrichedRows) && enrichedRows.length > 0) {
            enrichedRows.forEach(row => {
                if (row && typeof row === 'object') {
                    Object.keys(row).forEach(k => available.add(k));
                }
            });
        }

        // Helper to keep only columns that are both in merged list and present in enrichedRows
        const keepIfAvailable = (cols) => cols.filter(col => merged.includes(col) && available.has(col));

        // If no preset or Default — show merged columns that are available
        if (!preset || preset === 'Default') {
            const defaultFiltered = keepIfAvailable(merged);
            return defaultFiltered;
        }

        // For named preset, read stored order and apply same availability filter
        const storedPresets = getStoredPresets();
        const presetCols = Array.isArray(storedPresets[preset]) ? storedPresets[preset] : [];
        const filtered = keepIfAvailable(presetCols);

        // If preset yields nothing usable, fall back to default filtered merged list
        return filtered.length > 0 ? filtered : keepIfAvailable(merged);
    };

    // Get columns for download: return the full preset column list (preserve saved order).
    // If preset not found or empty, fall back to merged columns.
    const getColumnsForDownload = (preset) => {
        const merged = getMergedColumns();
        if (!preset || preset === 'Default') return merged;
        const storedPresets = getStoredPresets();
        const presetCols = Array.isArray(storedPresets[preset]) ? storedPresets[preset] : [];
        // keep only columns that exist in merged list to avoid unknown entries
        const filtered = presetCols.filter(col => merged.includes(col));
        return filtered.length > 0 ? filtered : merged;
    };

    const handlePresetChange = (e) => {
        setSelectedPreset(e.value);
    };

    const handleSettingsOpen = () => {
        setSettingsVisible(true);
    };

    const handleSettingsClose = () => {
        setSettingsVisible(false);
    };

    const handlePresetSaved = (name) => {
        loadPresets(); // Reload presets
        setSelectedPreset(name || 'Default');
        handleSettingsClose();
    };

    const handlePresetRemoved = (name) => {
        loadPresets(); // Reload presets
        setSelectedPreset('Default');
        handleSettingsClose();
    };

    // Default priorities sidebar visibility
    const [defaultSettingsVisible, setDefaultSettingsVisible] = useState(false);

    // Settings menu
    const settingsMenuRef = useRef(null);
    const settingsItems = [
        {
            label: 'Edit Default Priorities',
            icon: 'pi pi-sliders-h',
            command: () => setDefaultSettingsVisible(true)
        },
        {
            label: 'Apply Default Priorities',
            icon: 'pi pi-check',
            command: () => {
                if (typeof onApplyDefaultPriorities === 'function') {
                    onApplyDefaultPriorities();
                }
            }
        }
    ];

    return (
        <>
            <div
                style={{
                    marginTop: 0,
                    marginBottom: 12,
                    minHeight: 40,
                    display: 'grid',
                    gridTemplateColumns: '1fr auto 1fr',
                    alignItems: 'center'
                }}
            >
                <div /> {/* left spacer */}

                {/* center: Download button(s) */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                    <Button
                        label="Download"
                        icon="pi pi-download"
                        className="p-button-success"
                        onClick={() => {
                            const cols = getColumnsForDownload(selectedPreset);
                            handleDownload(cols);
                        }}
                    />
                    {handleDownloadAbu && (
                        <Button
                            label="Download ABU"
                            icon="pi pi-download"
                            className="p-button-warning"
                            onClick={() => {
                                handleDownloadAbu();
                            }}
                        />
                    )}
                </div>

                {/* right: preset label, dropdown and cog */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
                    <label style={{ marginRight: 6, fontSize: '0.9rem' }}>Excel export preset</label>
                    <Dropdown
                        value={selectedPreset}
                        options={presetOptions}
                        onChange={handlePresetChange}
                        placeholder="Select preset"
                        className="p-inputtext-sm"
                        style={{ minWidth: 160 }}
                    />
                    <Button
                        icon="pi pi-pencil"
                        className="p-button-secondary"
                        onClick={handleSettingsOpen}
                        aria-label="Edit Excel Preset"
                    />
                    <Button
                        icon="pi pi-cog"
                        className="p-button-secondary"
                        aria-label="Settings"
                        label="Settings"
                        onClick={(e) => settingsMenuRef.current?.toggle(e)}
                    />
                    <Menu model={settingsItems} popup ref={settingsMenuRef} style={{ minWidth: '240px' }} />
                </div>
            </div>

            <Sidebar
                header="Export settings"
                visible={settingsVisible}
                position="right"
                onHide={handleSettingsClose}
                baseZIndex={1000}
                style={{ width: '40%' }}
            >
                <ExportSettings
                    visible={settingsVisible}
                    onHide={handleSettingsClose}
                    // pass full merged column list so the settings UI always shows all columns
                    columns={getMergedColumns()}
                    initialPreset={selectedPreset}
                    onSaved={handlePresetSaved}
                    onRemoved={handlePresetRemoved}
                />
            </Sidebar>

            {/* Default Priorities Settings Sidebar */}
            <Sidebar
                header="Default source priorities"
                visible={defaultSettingsVisible}
                position="right"
                onHide={() => setDefaultSettingsVisible(false)}
                baseZIndex={1000}
                style={{ width: '60%' }}
            >
                <DefaultPrioritiesSettings
                    visible={defaultSettingsVisible}
                    onHide={() => setDefaultSettingsVisible(false)}
                    columns={getMergedColumns().filter(c => (c === "Units" || c === "VIN" || c === "FromSource") ? false : true)}
                />
            </Sidebar>

            <div style={{ fontSize: '0.9rem' }}>
                <div style={{ overflowX: 'auto', minHeight: '54vh', maxHeight: '54vh', overflowY: 'auto' }}>
                    <DataTable
                        value={enrichedRows}
                        scrollable
                        style={{
                            fontSize: '0.9em',
                            padding: 0,
                            margin: 0,
                            lineHeight: 1.2,
                            minWidth: 'max-content',
                        }}
                        showGridlines
                        rowClassName={(rowData) =>
                            rowData.Source === '1' ? 'enriched-yellow-row' : null
                        }
                    >
                        {(() => {
                            const colsToRender = getColumnsForPreset(selectedPreset);
                            return colsToRender.map(col => {
                                const isGrouping = Array.isArray(grouping) && grouping.includes(col);
                                return (
                                    <Column
                                        key={col}
                                        field={col}
                                        header={
                                            <span
                                                style={{
                                                    padding: '2px 6px',
                                                    fontSize: '0.95em',
                                                    fontWeight: isGrouping ? 600 : 400,
                                                }}
                                            >
                                                {col}
                                            </span>
                                        }
                                        style={{
                                            minWidth: 100,
                                            maxWidth: 500,
                                            padding: '2px 4px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                        headerStyle={{
                                            padding: '2px 4px',
                                            maxWidth: 500,
                                        }}
                                        body={(rowData) => customCell({ value: rowData[col], rowData, col })}
                                    />
                                );
                            });
                        })()}
                    </DataTable>
                </div>
            </div>
        </>
    );
};

export default EnrichmentResultTab;